import React, { useState, useRef } from 'react';
import Papa from 'papaparse';
import Button from './Button';
import Icon from '../AppIcon';

const CSVImportModal = ({ 
  isOpen, 
  onClose, 
  title, 
  templateData, 
  requiredColumns, 
  onImport,
  processingText = "Processing CSV...",
  successText = "imported successfully!"
}) => {
  const [file, setFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const selectedFile = event?.target?.files?.[0];
    if (selectedFile && selectedFile?.type === 'text/csv') {
      setFile(selectedFile);
      setError(null);
      setResults(null);
    } else {
      setError('Please select a valid CSV file');
      setFile(null);
    }
  };

  const validateCSVData = (data) => {
    if (!data || data?.length === 0) {
      return { isValid: false, error: 'CSV file is empty' };
    }

    const headers = Object.keys(data?.[0] || {});
    
    // Check if at least one required column exists instead of all required columns
    const hasRequiredColumns = requiredColumns?.some(col => headers?.includes(col));
    
    if (requiredColumns?.length > 0 && !hasRequiredColumns) {
      return { 
        isValid: false, 
        error: `Missing required columns: ${requiredColumns?.join(', ')}. Please check your CSV file and ensure it contains at least one of the required columns.` 
      };
    }

    // For validation, we'll be more flexible and allow any rows that have some valid data
    const validRows = data?.filter(row => {
      // If no required columns specified, any non-empty row is valid
      if (!requiredColumns || requiredColumns?.length === 0) {
        return Object.values(row)?.some(value => value && value?.toString()?.trim());
      }
      
      // Check if at least one required column has data
      return requiredColumns?.some(col => row?.[col] && row?.[col]?.toString()?.trim());
    });

    if (validRows?.length === 0) {
      return { 
        isValid: false, 
        error: 'No valid rows found. Please check that your CSV file contains data in the required columns.' 
      };
    }

    return { 
      isValid: true, 
      data: validRows,
      totalRows: data?.length,
      validRows: validRows?.length,
      skippedRows: data?.length - validRows?.length
    };
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a CSV file first');
      return;
    }

    setImporting(true);
    setError(null);

    try {
      Papa?.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: async (parseResults) => {
          try {
            const validation = validateCSVData(parseResults?.data);
            
            if (!validation?.isValid) {
              setError(validation?.error);
              setImporting(false);
              return;
            }

            const importResults = await onImport(validation?.data);
            
            if (importResults?.error) {
              setError(importResults?.error);
            } else {
              setResults({
                success: true,
                imported: importResults?.imported || validation?.validRows,
                skipped: validation?.skippedRows || 0,
                total: validation?.totalRows || validation?.data?.length
              });
              
              // Auto-close after 3 seconds on success
              setTimeout(() => {
                handleClose();
              }, 3000);
            }
          } catch (err) {
            setError(`Import failed: ${err?.message || 'Unknown error occurred'}`);
          } finally {
            setImporting(false);
          }
        },
        error: (parseError) => {
          setError(`Failed to parse CSV: ${parseError?.message}`);
          setImporting(false);
        }
      });
    } catch (err) {
      setError(`Import failed: ${err?.message || 'Unknown error occurred'}`);
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    if (!templateData || templateData?.length === 0) {
      setError('Template data not available');
      return;
    }

    const csv = Papa?.unparse(templateData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link?.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link?.setAttribute('href', url);
      link?.setAttribute('download', `${title?.toLowerCase()?.replace(/\s+/g, '_')}_template.csv`);
      link.style.visibility = 'hidden';
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
    }
  };

  const handleClose = () => {
    setFile(null);
    setError(null);
    setResults(null);
    setImporting(false);
    if (fileInputRef?.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-heading font-semibold text-gray-900">
              {title}
            </h2>
            <button
              onClick={handleClose}
              disabled={importing}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors disabled:opacity-50"
            >
              <Icon name="X" size={20} className="text-gray-500" />
            </button>
          </div>

          {/* Template Download */}
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <div className="flex items-start space-x-3">
              <Icon name="Download" size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-medium text-blue-900 mb-1">
                  Download CSV Template
                </h3>
                <p className="text-sm text-blue-700 mb-3">
                  Download the template to see the required format and columns.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={downloadTemplate}
                  iconName="Download"
                  iconPosition="left"
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  Download Template
                </Button>
              </div>
            </div>
          </div>

          {/* File Upload */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select CSV File
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                disabled={importing}
              />
              <div className="space-y-2">
                <Icon name="Upload" size={32} className="text-gray-400 mx-auto" />
                <div>
                  <button
                    type="button"
                    onClick={() => fileInputRef?.current?.click()}
                    disabled={importing}
                    className="text-indigo-600 hover:text-indigo-500 font-medium disabled:opacity-50"
                  >
                    Choose CSV file
                  </button>
                  <p className="text-gray-500">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">CSV files only</p>
              </div>
            </div>
            
            {file && (
              <div className="mt-2 flex items-center space-x-2 text-sm text-gray-600">
                <Icon name="File" size={16} />
                <span>{file?.name}</span>
                <span className="text-gray-400">({(file?.size / 1024)?.toFixed(1)} KB)</span>
              </div>
            )}
          </div>

          {/* Required Columns Info */}
          {requiredColumns && requiredColumns?.length > 0 && (
            <div className="mb-6 p-3 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Required Columns:</h4>
              <div className="flex flex-wrap gap-1">
                {requiredColumns?.map((column) => (
                  <span
                    key={column}
                    className="px-2 py-1 bg-gray-200 text-gray-700 text-xs rounded-full"
                  >
                    {column}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <Icon name="AlertTriangle" size={16} className="text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {/* Success Results */}
          {results?.success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <Icon name="CheckCircle" size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-green-800">
                  <p className="font-medium mb-1">Import Successful!</p>
                  <p>{results?.imported} {successText}</p>
                  {results?.skipped > 0 && (
                    <p className="text-green-600">{results?.skipped} rows skipped due to missing data</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <Button
              variant="danger"
              onClick={handleClose}
              disabled={importing}
              className="flex-1"
            >
              {results?.success ? 'Close' : 'Cancel'}
            </Button>
            
            {!results?.success && (
              <Button
                variant="default"
                onClick={handleImport}
                disabled={!file || importing}
                iconName={importing ? "Loader2" : "Upload"}
                iconPosition="left"
                className={`flex-1 ${importing ? 'animate-spin' : ''}`}
              >
                {importing ? processingText : 'Import CSV'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CSVImportModal;